/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import financialStatementDataService from "services/financialStatement";

// Async thunk for getting all financial statements
export const getAllFinancialStatementsAsync = createAsyncThunk(
  "financialStatements/getAll",
  async ({ queryParams }, { rejectWithValue }) => {
    try {
      const res = await financialStatementDataService.getAll(queryParams);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for creating a financial statement
export const createFinancialStatementAsync = createAsyncThunk(
  "financialStatements/create",
  async ({ data }, { rejectWithValue }) => {
    try {
      const res = await financialStatementDataService.create(data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for updating a financial statement
export const updateFinancialStatementAsync = createAsyncThunk(
  "financialStatements/update",
  async ({ financialStatementId, data }, { rejectWithValue }) => {
    try {
      const res = await financialStatementDataService.update(
        financialStatementId,
        data
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for getting a single financial statement
export const getFinancialStatementAsync = createAsyncThunk(
  "financialStatements/get",
  async ({ financialStatementId }, { rejectWithValue }) => {
    try {
      const res = await financialStatementDataService.get(financialStatementId);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for deleting a financial statement
export const deleteFinancialStatementAsync = createAsyncThunk(
  "financialStatements/delete",
  async ({ financialStatementId }, { rejectWithValue }) => {
    try {
      const res = await financialStatementDataService.remove(
        financialStatementId
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  financialStatements: {},
  getAllFinancialStatementsStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  createFinancialStatementStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  updateFinancialStatementStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  getFinancialStatementStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  deleteFinancialStatementStatus: {
    status: "idle",
    error: false,
    data: null,
  },
};

const financialStatementsSlice = createSlice({
  name: "financialStatements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllFinancialStatementsAsync.pending, (state) => {
        state.getAllFinancialStatementsStatus.status = "loading";
        state.getAllFinancialStatementsStatus.error = false;
        state.getAllFinancialStatementsStatus.data = null;
      })
      .addCase(getAllFinancialStatementsAsync.fulfilled, (state, action) => {
        state.getAllFinancialStatementsStatus.status = "idle";
        state.getAllFinancialStatementsStatus.error = false;
        state.getAllFinancialStatementsStatus.data = action.payload;
        state.financialStatements = action.payload;
      })
      .addCase(getAllFinancialStatementsAsync.rejected, (state, action) => {
        state.getAllFinancialStatementsStatus.status = "failed";
        state.getAllFinancialStatementsStatus.error =
          action.payload.messageText || "Failed to fetch financial statements";
      })
      .addCase(createFinancialStatementAsync.pending, (state) => {
        state.createFinancialStatementStatus.status = "loading";
        state.createFinancialStatementStatus.error = false;
        state.createFinancialStatementStatus.data = null;
      })
      .addCase(createFinancialStatementAsync.fulfilled, (state, action) => {
        state.createFinancialStatementStatus.status = "idle";
        state.createFinancialStatementStatus.error = false;
        state.createFinancialStatementStatus.data = action.payload;
        state.financialStatements = action.payload;
      })
      .addCase(createFinancialStatementAsync.rejected, (state, action) => {
        state.createFinancialStatementStatus.status = "failed";
        state.createFinancialStatementStatus.error =
          action.payload.messageText || "Failed to create financial statement";
      })
      .addCase(updateFinancialStatementAsync.pending, (state) => {
        state.updateFinancialStatementStatus.status = "loading";
        state.updateFinancialStatementStatus.error = false;
        state.updateFinancialStatementStatus.data = null;
      })
      .addCase(updateFinancialStatementAsync.fulfilled, (state, action) => {
        state.updateFinancialStatementStatus.status = "idle";
        state.updateFinancialStatementStatus.error = false;
        state.updateFinancialStatementStatus.data = action.payload;
        state.financialStatements = {
          ...state.financialStatements,
          ...action.payload,
        };
      })
      .addCase(updateFinancialStatementAsync.rejected, (state, action) => {
        state.updateFinancialStatementStatus.status = "failed";
        state.updateFinancialStatementStatus.error =
          action.payload.messageText || "Failed to update financial statement";
      })
      .addCase(getFinancialStatementAsync.pending, (state) => {
        state.getFinancialStatementStatus.status = "loading";
        state.getFinancialStatementStatus.error = false;
        state.getFinancialStatementStatus.data = null;
      })
      .addCase(getFinancialStatementAsync.fulfilled, (state, action) => {
        state.getFinancialStatementStatus.status = "idle";
        state.getFinancialStatementStatus.error = false;
        state.getFinancialStatementStatus.data = action.payload;
        state.financialStatements = action.payload;
      })
      .addCase(getFinancialStatementAsync.rejected, (state, action) => {
        state.getFinancialStatementStatus.status = "failed";
        state.getFinancialStatementStatus.error =
          action.payload.messageText || "Failed to fetch financial statement";
      })
      .addCase(deleteFinancialStatementAsync.pending, (state) => {
        state.deleteFinancialStatementStatus.status = "loading";
        state.deleteFinancialStatementStatus.error = false;
        state.deleteFinancialStatementStatus.data = null;
      })
      .addCase(deleteFinancialStatementAsync.fulfilled, (state, action) => {
        state.deleteFinancialStatementStatus.status = "idle";
        state.deleteFinancialStatementStatus.error = false;
        state.deleteFinancialStatementStatus.data = action.payload;
        delete state.financialStatements[action.payload._id];
      })
      .addCase(deleteFinancialStatementAsync.rejected, (state, action) => {
        state.deleteFinancialStatementStatus.status = "failed";
        state.deleteFinancialStatementStatus.error =
          action.payload.messageText || "Failed to delete financial statement";
      });
  },
});

export default financialStatementsSlice.reducer;
